// Client-side aggregations that mirror the backend /api/breakdown/* endpoints.
//
// Why client-side: the SPA already fetches the full corpus via /api/data at
// boot. Computing breakdowns in JS is sub-10ms at 8k rows and gives instant
// UX on tab switch + filter change. The backend endpoints (added in SGNL-57)
// remain the canonical source of truth, available for external clients or
// when corpus growth pushes us to server-side filtering.
//
// Schema parity: the row shapes here mirror the Pydantic schemas in
// `backend/app/schemas/breakdown.py` exactly. If the backend shape changes,
// update both sides + their tests together.
//
// Classification convention (selling-precedence, corrected 2026-05-10):
//   "Selling AI"  ai_boolean is TRUE  (the deliverable IS an AI service)
//   "Using AI"    ai_boolean is NOT TRUE AND ai_enablement is non-empty
//   "Non-AI"      neither
// Pre-fix the precedence was inverted — see classifyAI's comment for
// bug history. SGNL-127 (R4 LLM verdict surfacing) is the longer-term
// replacement; backend keeps an exact mirror of this rule in
// `_ai_class_won_amt`.

import type { AIClass, OpportunityRow } from "../types";
import { MS_PER_DAY } from "./format";

// SGNL-109 — Tag-classifier confidence threshold pinned in code, not UI.
// Operators reasoned about "is the tag on or off", not about confidence
// fractions; the prior `conf ≥` filter input was confusing and got removed.
//
// SGNL-127 — raised from 0.6 → 0.70 to match the server-side
// `Settings.tag_confidence_threshold` used by the LLM-derived AI
// columns. Pre-fix the two constants disagreed: a 0.65 yes-verdict
// would show as a Tag chip (FE 0.6) but the matching "Selling AI
// (LLM)" column (BE 0.70) would be blank, which read as a UI bug to
// operators. 0.70 is also the better value from the prod confidence
// distribution analysis (May 2026): the AI tags have a hard floor
// around 0.70 anyway (~99% of yes-verdicts clear it). Display-only
// value — the SQL gate on /api/data is the backend setting. A future
// PR can serve the threshold via meta so the constant can stop
// duplicating server config (out of scope here).
export const TAG_CONFIDENCE_THRESHOLD = 0.7;

// ---------- Shared types (mirror backend Pydantic schemas) ----------

export interface AIBreakdownRow {
  bu: string | null;
  deals: number;
  customers: number;
  selling_ai_won: number;
  using_ai_won: number;
  non_ai_won: number;
  won: number;
  lost: number;
  open: number;
  win_rate: number | null;
}

export interface FarmingBreakdownRow {
  bu: string | null;
  type: string | null;
  deals: number;
  won_n: number;
  lost_n: number;
  won: number;
  lost: number;
  open: number;
  win_rate: number | null;
}

export interface TopCustomerRow {
  account_id: string | null;
  account_name: string | null;
  bu: string | null;
  industry: string | null;
  deals: number;
  won: number;
  lost: number;
  open: number;
  win_rate: number | null;
}

export type HygieneBucket = "fresh" | "stale" | "old";

export interface HygieneRow {
  id: string;
  name: string;
  bu: string | null;
  account_name: string | null;
  owner_name: string | null;
  stage: string | null;
  age_bucket: HygieneBucket;
  days_since_update: number | null;
  issues: string[];
}

// SGNL-214 — `LostBreakdownRow` + `LostDealRow` removed alongside their
// computeLostBreakdown / listLostDeals helpers. The Aggregated/Detailed
// toggle they backed is replaced by the new Lost Deal Analysis surface
// (4 collapsible analytic sections — see views/lost.ts). Re-add only if
// a future caller needs the (BU, reason) rollup or the per-deal listing
// (the latter is now better served by Deal Explorer's #deals?view=lost).

// ---------- Helpers ----------

const HYGIENE_STALE_DAYS = 60;
const HYGIENE_OLD_DAYS = 120;

// SGNL-222 — canonical issue-string literals emitted by
// computeHygieneBuckets. Exported so lib/hygiene_filters.ts predicates
// can match them by identity rather than by re-typing the string. If
// one of these constants is renamed the chip predicate updates with
// it; the compiler enforces parity instead of a silent always-zero
// chip. Per architect-MED on this PR.
export const HYGIENE_ISSUE_MISSING_CLOSE_DATE = "missing close_date";
export const HYGIENE_ISSUE_MISSING_AMOUNT = "missing amount";
export const HYGIENE_ISSUE_MISSING_NEXT_STEP = "missing next step";
export const HYGIENE_ISSUE_NO_MOD_DATE = "no last_modified_date";
export const HYGIENE_ISSUE_INVALID_MOD_DATE = "invalid last_modified_date";

// `stale N+ days` is the template prefix; the trailing day count is
// dynamic. Callers that want to match the family of stale-N strings
// should test `r.age_bucket === "stale" || r.age_bucket === "old"`
// rather than substring-matching the issue text (cheaper + canonical).

// Defensive upper bound on `daysSince` before it's interpolated into
// the issue string. The value is derived from SF-supplied timestamps
// — a clock-skewed or malformed date could otherwise produce
// arbitrarily large numerals that widen the badge visually. Per
// security-LOW on this PR. esc() blocks any XSS vector regardless.
const HYGIENE_DAYS_DISPLAY_CAP = 9999;

// Precedence (corrected from the inverted pre-2026-05-10 logic; see
// `backend/app/services/queries.py::_ai_class_won_amt` for the matching
// SQL):
//   Selling AI = ai_boolean is TRUE  (the deliverable IS an AI service)
//   Using AI   = ai_boolean is NOT TRUE AND ai_enablement is non-empty
//   Non-AI     = neither
function isSellingAI(r: OpportunityRow): boolean {
  return r.ai_boolean === true;
}

function isUsingAI(r: OpportunityRow): boolean {
  // PR-Agent finding (importance 5) on PR #68: Salesforce occasionally
  // returns whitespace-only ai_enablement. Strip before the empty
  // check so frontend matches backend's _rep_bucket_for. Both surfaces
  // now treat "   " as non-AI, not Using.
  return (
    r.ai_boolean !== true
    && r.ai_enablement !== null
    && (r.ai_enablement ?? "").trim() !== ""
  );
}

// SGNL-109 — exported as the canonical AI classifier. Was duplicated in
// state.ts; kept singular here so kpis.ts and applyFilters can't drift
// from the BU-aggregation rules.
//
// Selling-precedence: a deal where the rep set BOTH ai_boolean=true AND
// ai_enablement="..." is Selling, not Using. The headline question is
// "is the deliverable AI?" (ai_boolean); ai_enablement is the
// secondary "are we using AI to deliver it?" tag. Pre-fix the
// precedence was inverted, mis-classifying genuine Selling deals as
// Using when reps populated both fields.
export function classifyAI(r: OpportunityRow): AIClass {
  if (isSellingAI(r)) return "selling";
  if (isUsingAI(r)) return "using";
  return "non";
}

// SGNL-127 — LLM verdict from opportunity_tags, server-side gated by
// `Settings.tag_confidence_threshold`. Returns "non" when neither
// AI:Solution nor AI:Delivery passed the threshold (treats absence as
// negative — for "no verdict at all", read `classified_at`).
export function classifyLLMAI(r: OpportunityRow): "selling-llm" | "using-llm" | "non" {
  // Selling-precedence mirrors the rep-side rule: a deal that's BOTH
  // selling-AI (the product is AI) AND using-AI (the team uses Copilot)
  // surfaces as Selling, because the headline question is what the
  // deliverable is. The breakdown 'using_ai_llm' bucket is therefore
  // "team uses AI but the deliverable isn't AI" — symmetric with the
  // rep-side aggregation.
  if (r.llm_ai_solution === true) return "selling-llm";
  if (r.llm_ai_delivery === true) return "using-llm";
  return "non";
}

// SGNL-127 — opps where the rep classification disagrees with the LLM.
// Maps each side to its "Selling / Using / Non" tri-state and flags a
// mismatch. These are the audit-and-write-back candidates: either the
// rep forgot to tag a real AI deal, or the rep over-tagged a non-AI
// deal as AI. Disagree never includes opps that the LLM hasn't
// classified yet (classified_at null) — that'd swamp the bucket with
// noise from the backlog. Same exclusion applies if rep tagging is
// missing entirely.
export function llmRepDisagree(r: OpportunityRow): boolean {
  // Skip rows we cannot reliably compare. classified_at null = LLM
  // hasn't run on this opp; ai_boolean null = rep tagging missing.
  //
  // PR-Agent review_guide on PR #59 flagged that classified_at could
  // theoretically be set by a different process than the AI-tag
  // classifier. Verified at classifier.py:380 — `opp.classified_at`
  // is stamped in the SAME function (`_persist_classification`) that
  // writes the AI:Solution / AI:Delivery rows to opportunity_tags.
  // There is no other writer in the codebase; the gate is safe.
  //
  // PR-Agent improve v2 (importance 5): also skip rows where the
  // llm_ai_* booleans are missing from the response (an older
  // serialized bundle in localStorage, a future BE that drops the
  // fields, etc.). Pre-fix, undefined llm_ai_* + classified_at
  // present would map classifyLLMAI to 'non', producing false
  // disagree positives against rep=selling/using. Treat any
  // undefined as "not classified for LLM" — same semantics as
  // classified_at null.
  if (r.classified_at == null) return false;
  if (r.ai_boolean === null || r.ai_boolean === undefined) return false;
  // BOTH undefined => historical bundle that predates the LLM fields.
  // ONE undefined is fine: if one is `true` (or `false`) we have a
  // valid LLM verdict; the missing one defaults to false in
  // classifyLLMAI's `=== true` check. The combined-undefined case is
  // the false-disagree risk.
  if (r.llm_ai_solution === undefined && r.llm_ai_delivery === undefined) {
    return false;
  }
  return classifyAI(r) !== mapLLMToRepLabel(classifyLLMAI(r));
}

function mapLLMToRepLabel(c: "selling-llm" | "using-llm" | "non"): AIClass {
  if (c === "selling-llm") return "selling";
  if (c === "using-llm") return "using";
  return "non";
}

function isWon(r: OpportunityRow): boolean {
  return r.is_won === true;
}

// Tightened predicate (PR-Agent finding on PR #10): closed-with-null-is_won
// is ambiguous data — neither won nor lost. Treating it as lost would skew
// totals and win-rates when upstream data is incomplete. Backend ingest
// populates is_won as a non-null boolean for any closed opp; if we ever
// drop that invariant the user-visible counts here are the safer bet.
function isLost(r: OpportunityRow): boolean {
  return r.is_closed === true && r.is_won === false;
}

function isOpen(r: OpportunityRow): boolean {
  return r.is_closed !== true;
}

function winRate(wonN: number, lostN: number): number | null {
  const closed = wonN + lostN;
  return closed > 0 ? wonN / closed : null;
}

function daysBetween(later: Date, earlier: Date): number {
  return Math.max(0, Math.floor((later.getTime() - earlier.getTime()) / MS_PER_DAY));
}

// ---------- AI breakdown ----------

/**
 * Group opps by BU; split Won $ by Selling AI / Using AI / Non-AI.
 *
 * PLACEHOLDER (R1.5): classification is rule-based on rep-tagged fields.
 * SGNL-4 (R4 / LLM multi-tag classification) replaces this with LLM tags.
 */
// SGNL-127 — projection a row's classification onto the three AI buckets
// the breakdown table tracks: 'selling' (deliverable IS AI), 'using'
// (team uses AI internally; deliverable isn't AI), 'non' (neither).
// The breakdown table is symmetric across rep and LLM classifiers — the
// only thing that changes is HOW we decide which bucket a row falls into.
type AIBucket = "selling" | "using" | "non";

function repBucket(r: OpportunityRow): AIBucket {
  // classifyAI's return type widened to AIClass when SGNL-127 added
  // the LLM-variant values, but its body only ever returns the three
  // rep tokens. Narrow back here so the bucket lookup is exhaustive
  // and TypeScript can prove it.
  const c = classifyAI(r);
  return c === "selling" || c === "using" ? c : "non";
}

function llmBucket(r: OpportunityRow): AIBucket {
  const c = classifyLLMAI(r);
  if (c === "selling-llm") return "selling";
  if (c === "using-llm") return "using";
  return "non";
}

// SGNL-127 — single aggregator parameterised by classifier. The rep and
// LLM subviews share the same table shape; what changes between them is
// which classifier maps row → AI bucket. Pre-fix, computeAIBreakdown
// hard-coded classifyAI; now it accepts a classifier so the LLM subview
// is one call away.
export function computeAIBreakdownBy(
  rows: OpportunityRow[],
  classify: (r: OpportunityRow) => AIBucket,
): AIBreakdownRow[] {
  const buckets = new Map<
    string | null,
    {
      deals: number;
      accounts: Set<string>;
      sellingWon: number;
      usingWon: number;
      nonWon: number;
      won: number;
      lost: number;
      open: number;
      wonN: number;
      lostN: number;
    }
  >();
  for (const r of rows) {
    let b = buckets.get(r.bu);
    if (!b) {
      b = {
        deals: 0,
        accounts: new Set(),
        sellingWon: 0,
        usingWon: 0,
        nonWon: 0,
        won: 0,
        lost: 0,
        open: 0,
        wonN: 0,
        lostN: 0,
      };
      buckets.set(r.bu, b);
    }
    b.deals++;
    if (r.account_id) b.accounts.add(r.account_id);
    const amt = r.amount ?? 0;
    if (isWon(r)) {
      b.won += amt;
      b.wonN++;
      const klass = classify(r);
      if (klass === "selling") b.sellingWon += amt;
      else if (klass === "using") b.usingWon += amt;
      else b.nonWon += amt;
    } else if (isLost(r)) {
      b.lost += amt;
      b.lostN++;
    } else if (isOpen(r)) {
      b.open += amt;
    }
  }
  return [...buckets.entries()]
    .map(([bu, b]) => ({
      bu,
      deals: b.deals,
      customers: b.accounts.size,
      selling_ai_won: b.sellingWon,
      using_ai_won: b.usingWon,
      non_ai_won: b.nonWon,
      won: b.won,
      lost: b.lost,
      open: b.open,
      win_rate: winRate(b.wonN, b.lostN),
    }))
    .sort((a, b) => b.won - a.won);
}

// Back-compat alias. The rep-tagged breakdown is the original /
// historical default; existing callers (charts, drill links) keep
// using `computeAIBreakdown`. SGNL-127 introduces the LLM sibling.
export function computeAIBreakdown(rows: OpportunityRow[]): AIBreakdownRow[] {
  return computeAIBreakdownBy(rows, repBucket);
}

// SGNL-127 — LLM-classified breakdown. Same shape, same per-BU rows,
// same Won/Lost/Open columns; the difference is the Selling / Using /
// Non bucketing comes from `opportunity_tags` verdicts (server-gated
// by confidence threshold) instead of rep-tagged fields.
export function computeLLMAIBreakdown(rows: OpportunityRow[]): AIBreakdownRow[] {
  return computeAIBreakdownBy(rows, llmBucket);
}

// ---------- Farming vs Hunting ----------

export function computeFarmingBreakdown(rows: OpportunityRow[]): FarmingBreakdownRow[] {
  const buckets = new Map<
    string,
    {
      bu: string | null;
      type: string | null;
      deals: number;
      wonN: number;
      lostN: number;
      won: number;
      lost: number;
      open: number;
    }
  >();
  for (const r of rows) {
    const key = `${r.bu ?? ""}::${r.type ?? ""}`;
    let b = buckets.get(key);
    if (!b) {
      b = {
        bu: r.bu,
        type: r.type,
        deals: 0,
        wonN: 0,
        lostN: 0,
        won: 0,
        lost: 0,
        open: 0,
      };
      buckets.set(key, b);
    }
    b.deals++;
    const amt = r.amount ?? 0;
    if (isWon(r)) {
      b.won += amt;
      b.wonN++;
    } else if (isLost(r)) {
      b.lost += amt;
      b.lostN++;
    } else if (isOpen(r)) {
      b.open += amt;
    }
  }
  return [...buckets.values()]
    .map((b) => ({
      bu: b.bu,
      type: b.type,
      deals: b.deals,
      won_n: b.wonN,
      lost_n: b.lostN,
      won: b.won,
      lost: b.lost,
      open: b.open,
      win_rate: winRate(b.wonN, b.lostN),
    }))
    .sort((a, b) => b.won - a.won);
}

// ---------- Top customers ----------

export function computeTopCustomers(
  rows: OpportunityRow[],
  limit = 50,
): TopCustomerRow[] {
  const buckets = new Map<
    string,
    {
      account_id: string | null;
      account_name: string | null;
      bu: string | null;
      industry: string | null;
      deals: number;
      wonN: number;
      lostN: number;
      won: number;
      lost: number;
      open: number;
    }
  >();
  for (const r of rows) {
    const key = r.account_id ?? `__noacct_${r.id}`;
    let b = buckets.get(key);
    if (!b) {
      b = {
        account_id: r.account_id,
        account_name: r.account_name,
        bu: r.bu,
        industry: r.industry,
        deals: 0,
        wonN: 0,
        lostN: 0,
        won: 0,
        lost: 0,
        open: 0,
      };
      buckets.set(key, b);
    }
    b.deals++;
    const amt = r.amount ?? 0;
    if (isWon(r)) {
      b.won += amt;
      b.wonN++;
    } else if (isLost(r)) {
      b.lost += amt;
      b.lostN++;
    } else if (isOpen(r)) {
      b.open += amt;
    }
  }
  return [...buckets.values()]
    .map((b) => ({
      account_id: b.account_id,
      account_name: b.account_name,
      bu: b.bu,
      industry: b.industry,
      deals: b.deals,
      won: b.won,
      lost: b.lost,
      open: b.open,
      win_rate: winRate(b.wonN, b.lostN),
    }))
    .sort((a, b) => b.won - a.won)
    .slice(0, limit);
}

// ---------- Data hygiene ----------

/**
 * Flag OPEN opportunities with stale or missing fields. Closed opps are
 * excluded — no point flagging hygiene on already-closed deals.
 *
 * `now` parameter for testability (default: actual current time).
 */
export function computeHygieneBuckets(
  rows: OpportunityRow[],
  now: Date = new Date(),
): HygieneRow[] {
  const out: HygieneRow[] = [];
  for (const r of rows) {
    if (!isOpen(r)) continue;

    const issues: string[] = [];
    if (r.close_date === null) issues.push(HYGIENE_ISSUE_MISSING_CLOSE_DATE);
    if (r.amount === null) issues.push(HYGIENE_ISSUE_MISSING_AMOUNT);
    if (!r.next_step) issues.push(HYGIENE_ISSUE_MISSING_NEXT_STEP);

    let daysSince: number | null = null;
    let bucket: HygieneBucket;
    if (r.last_modified_date !== null) {
      const lm = new Date(r.last_modified_date);
      // Guard against malformed timestamp strings — `new Date(...)` returns
      // Invalid Date silently, which then propagates NaN through bucketing
      // and sorting. Bucket invalid dates with the "no mod date" cohort.
      if (Number.isNaN(lm.getTime())) {
        bucket = "old";
        issues.push(HYGIENE_ISSUE_INVALID_MOD_DATE);
      } else {
        daysSince = daysBetween(now, lm);
        // Clamp the rendered count (not the bucketing decision) so a
        // pathological SF timestamp can't widen the issue badge to
        // 16 digits. esc() blocks any XSS path independently; this is
        // belt-and-braces for visual integrity. Per security-LOW.
        const displayDays = Math.min(daysSince, HYGIENE_DAYS_DISPLAY_CAP);
        if (daysSince >= HYGIENE_OLD_DAYS) {
          bucket = "old";
          issues.push(`stale ${displayDays}+ days`);
        } else if (daysSince >= HYGIENE_STALE_DAYS) {
          bucket = "stale";
          issues.push(`stale ${displayDays}+ days`);
        } else {
          bucket = "fresh";
        }
      }
    } else {
      bucket = "old";
      issues.push(HYGIENE_ISSUE_NO_MOD_DATE);
    }

    if (issues.length === 0) continue;
    out.push({
      id: r.id,
      name: r.name,
      bu: r.bu,
      account_name: r.account_name,
      owner_name: r.owner_name,
      stage: r.stage,
      age_bucket: bucket,
      days_since_update: daysSince,
      issues,
    });
  }
  // Sort by age (oldest first) so the worst hygiene shows up first.
  // Use Number.MAX_SAFE_INTEGER instead of Infinity for the null-bucket so
  // `b - a` can't produce NaN (Infinity - Infinity), which would corrupt
  // the sort order.
  const NEVER = Number.MAX_SAFE_INTEGER;
  return out.sort(
    (a, b) => (b.days_since_update ?? NEVER) - (a.days_since_update ?? NEVER),
  );
}

// ---------- Lost deals ----------

// ---------- SGNL-214 — Lost Deal Analysis ----------
//
// Four analytic shapes built on top of the closed-lost subset. Each
// helper takes the FULL opp list (the caller hasn't pre-filtered for
// `isLost`); the helpers gate by `isLost(r) && probability >= min`
// internally so the same caller can vary the `min` threshold without
// rebuilding the lost subset every time.
//
// The `min` argument is the "Qualified+" cutoff (default 5%) — drops
// MQL / Stage-0 noise from the analytic surfaces. Pass 0 (or null
// from the dropdown's "All" preset) to disable the cap.

export interface LostReasonRow {
  reason: string;
  count: number;
  amount: number;
}

export interface LostStageBucket {
  /** Bucket label rendered in the histogram axis. */
  label: string;
  /** Inclusive lower probability bound. */
  min: number;
  /** Exclusive upper probability bound; +Infinity for the last bucket. */
  max: number;
  count: number;
  amount: number;
}

export interface LostByAccountRow {
  account_name: string;
  count: number;
  amount: number;
}

/** SGNL-219 — row in the lost-from-stage histogram on the multi-deal
 *  aggregate panel. Groups by the literal `pre_close_max_stage` string
 *  (not by probability bucket) because operators want to see "lost from
 *  Final Negotiation" / "lost from Discovery" labels, not "70-89%". */
export interface LostByPreCloseStageRow {
  /** The pre-close peak stage label, or "(no history)" when the opp's
   *  aggregate is NULL (~2.5% of CL opps in production). */
  stage: string;
  count: number;
  amount: number;
}

export interface LostActivityGapBucket {
  /** Bucket label like "0–7d" / "8–30d" / "31–90d" / ">90d" / "unknown". */
  label: string;
  /** Inclusive lower-bound in days; null = "unknown" bucket. */
  min_days: number | null;
  /** Exclusive upper-bound; +Infinity for the open-ended bucket. */
  max_days: number;
  count: number;
  amount: number;
}

/** Numeric guard — accepts only finite numbers (rejects null /
 *  undefined / strings / NaN / Infinity). Mirrors the SGNL-210
 *  bucketTotalDollars guard so analytic totals don't silently absorb
 *  schema drift. */
function isFiniteProb(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

/** Probability-specific guard — additionally bounds-checks to [0,100].
 *  Pre-PR security LOW: a BE field with probability=110 would pass
 *  the plain `isFiniteProb` check and inflate the weighted total
 *  by 10%. The forecast formula multiplies by `probability/100`, so
 *  an out-of-range value silently overstates revenue. Use this guard
 *  wherever probability is being arithmetically applied. */
function isBoundedProbability(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 100;
}

/** Apply the SGNL-214 default filter: close_lost AND probability ≥ min.
 *  When `min` is null ("All" preset), only the close-lost predicate
 *  applies. Rows with non-finite probability are excluded WHEN
 *  `min !== null` (so MQL/0% noise can't sneak in via type drift). */
export function filterQualifiedLost(
  rows: readonly OpportunityRow[],
  min: number | null,
): OpportunityRow[] {
  return rows.filter((r) => {
    if (!isLost(r)) return false;
    if (min === null) return true;
    return isFiniteProb(r.probability) && r.probability >= min;
  });
}

/** Top loss reasons sorted by count desc, then $ amount desc. */
export function computeTopLossReasons(
  rows: readonly OpportunityRow[],
  min: number | null,
): LostReasonRow[] {
  const lost = filterQualifiedLost(rows, min);
  const buckets = new Map<string, LostReasonRow>();
  for (const r of lost) {
    const reason = (r.reason_no_ai ?? "").trim() || "(unknown)";
    let b = buckets.get(reason);
    if (!b) {
      b = { reason, count: 0, amount: 0 };
      buckets.set(reason, b);
    }
    b.count++;
    b.amount += isFiniteProb(r.amount) ? r.amount : 0;
  }
  return [...buckets.values()].sort(
    (a, b) => b.count - a.count || b.amount - a.amount,
  );
}

/** Probability-bucket histogram — proxies stage-at-loss using SF's
 *  canonical stage→probability mapping. Buckets:
 *    5–29%  (Discovery)
 *    30–49% (Qualifying)
 *    50–69% (Proposing)
 *    70–89% (Negotiating)
 *    90–99% (Closing)
 *  Rows below the active `min` are excluded entirely. The buckets
 *  themselves start at max(5, min) so a "Mid-stage+" preset doesn't
 *  surface an empty Discovery bucket.
 *
 *  @see computeLostByPreCloseStage for the literal-stage-string variant
 *    (groups by `pre_close_max_stage` rather than probability ranges) —
 *    these serve different UI surfaces and intentionally coexist. */
export function computeLostByStage(
  rows: readonly OpportunityRow[],
  min: number | null,
): LostStageBucket[] {
  const lower = min ?? 0;
  const ALL_BUCKETS: ReadonlyArray<Pick<LostStageBucket, "label" | "min" | "max">> = [
    { label: "Discovery (5–29%)",   min: 5,  max: 30 },
    { label: "Qualifying (30–49%)", min: 30, max: 50 },
    { label: "Proposing (50–69%)",  min: 50, max: 70 },
    { label: "Negotiating (70–89%)",min: 70, max: 90 },
    { label: "Closing (90–99%)",    min: 90, max: 100 },
  ];
  const out: LostStageBucket[] = ALL_BUCKETS
    .filter((b) => b.max > lower)
    .map((b) => ({ ...b, count: 0, amount: 0 }));
  // PR-Agent #131 (imp 5): under the "All" preset, surface null /
  // non-finite probability rows in an explicit "Unknown" bucket
  // instead of silently dropping them — otherwise the histogram
  // disagrees with the tab-level total and hides data-quality
  // issues. Only added when min === null; when a cap is active the
  // filterQualifiedLost call has already excluded non-finite rows.
  const unknownBucket: LostStageBucket | null = min === null
    ? { label: "Unknown probability", min: -1, max: 0, count: 0, amount: 0 }
    : null;
  for (const r of filterQualifiedLost(rows, min)) {
    if (!isFiniteProb(r.probability)) {
      if (unknownBucket) {
        unknownBucket.count++;
        unknownBucket.amount += isFiniteProb(r.amount) ? r.amount : 0;
      }
      continue;
    }
    for (const b of out) {
      if (r.probability >= b.min && r.probability < b.max) {
        b.count++;
        b.amount += isFiniteProb(r.amount) ? r.amount : 0;
        break;
      }
    }
  }
  if (unknownBucket && unknownBucket.count > 0) out.push(unknownBucket);
  return out;
}

/** Default cap for `computeLostByAccount`. Hoisted out of the
 *  function signature per code-review LOW — keeps the magic number
 *  out of inline call sites. */
export const LOST_BY_ACCOUNT_DEFAULT_LIMIT = 20;

/** Top customers (account_name) losing deals. Sorted by count desc
 *  then $ amount desc. Capped to `limit` rows so a single mega-tenant
 *  account doesn't drown the table — operators dig deeper via the
 *  account drill-through. */
export function computeLostByAccount(
  rows: readonly OpportunityRow[],
  min: number | null,
  limit = LOST_BY_ACCOUNT_DEFAULT_LIMIT,
): LostByAccountRow[] {
  // PR-Agent #131 (imp 4): clamp `limit` to a finite, non-negative
  // integer. A negative `limit` would make `slice(0, -2)` return
  // almost the full list (silently defeating the cap); a non-finite
  // value would slice nothing. Normalise here so the cap contract
  // is enforced regardless of the caller.
  const safeLimit = Number.isFinite(limit) && limit > 0
    ? Math.floor(limit)
    : LOST_BY_ACCOUNT_DEFAULT_LIMIT;
  const lost = filterQualifiedLost(rows, min);
  const buckets = new Map<string, LostByAccountRow>();
  for (const r of lost) {
    const account = r.account_name?.trim() || "(unknown)";
    let b = buckets.get(account);
    if (!b) {
      b = { account_name: account, count: 0, amount: 0 };
      buckets.set(account, b);
    }
    b.count++;
    b.amount += isFiniteProb(r.amount) ? r.amount : 0;
  }
  return [...buckets.values()]
    .sort((a, b) => b.count - a.count || b.amount - a.amount)
    .slice(0, safeLimit);
}

/** Distribution of "days between last_activity_date and close_date" —
 *  the time-since-last-conversation gap when the opp closed lost.
 *  High gap = stale opp died from neglect; low gap = competitive loss
 *  on an active deal. Buckets are intentionally coarse so the
 *  histogram tells a story at a glance.
 *
 *  "unknown" rows have either no last_activity_date or no close_date. */
export function computeLastActivityGap(
  rows: readonly OpportunityRow[],
  min: number | null,
): LostActivityGapBucket[] {
  const BUCKETS: ReadonlyArray<Omit<LostActivityGapBucket, "count" | "amount">> = [
    { label: "0–7d",   min_days: 0,    max_days: 8 },
    { label: "8–30d",  min_days: 8,    max_days: 31 },
    { label: "31–90d", min_days: 31,   max_days: 91 },
    { label: ">90d",   min_days: 91,   max_days: Infinity },
    { label: "unknown",min_days: null, max_days: 0 },
  ];
  const out: LostActivityGapBucket[] = BUCKETS.map((b) => ({ ...b, count: 0, amount: 0 }));
  for (const r of filterQualifiedLost(rows, min)) {
    const close = r.close_date ? new Date(r.close_date) : null;
    const lastAct = r.last_activity_date ? new Date(r.last_activity_date) : null;
    let bucket: LostActivityGapBucket;
    if (!close || !lastAct || Number.isNaN(close.getTime()) || Number.isNaN(lastAct.getTime())) {
      bucket = out[out.length - 1];  // "unknown"
    } else {
      // PR-review CR LOW: use `Math.floor` to match the existing
      // `daysBetween` helper (line ~246). With `Math.round`, a 7.5-day
      // gap rounds up to 8 and falls into "8–30d" — the bucket
      // labels imply a hard ≤7 cutoff. Floor gives that.
      const days = Math.max(0, Math.floor((close.getTime() - lastAct.getTime()) / MS_PER_DAY));
      const match = out.find((b) =>
        b.min_days !== null && days >= b.min_days && days < b.max_days,
      ) ?? out[out.length - 1];
      bucket = match;
    }
    bucket.count++;
    bucket.amount += isFiniteProb(r.amount) ? r.amount : 0;
  }
  return out;
}

/** SGNL-219 — group closed-lost opps by their pre_close_max_stage
 *  string (the historical peak stage the daily ingest computes).
 *  Used by the multi-deal aggregate analysis panel on the Lost tab.
 *
 *  Gates internally on `isLost` so a caller that passes the full
 *  bundle (e.g. a future BU dashboard reusing this helper) can't
 *  silently include Won / Open opps. Mirrors the sibling helpers
 *  (`computeTopLossReasons`, `computeLastActivityGap`,
 *  `computeLostByAccount`) which all use `filterQualifiedLost` for
 *  the same contract. Pre-PR code-reviewer MED.
 *
 *  Sort: count DESC, then $ amount DESC. The "(no history)" bucket
 *  surfaces opps whose pre_close_max_stage is NULL (~2.5% of CL opps
 *  in production) so they're not silently dropped from the totals.
 *  Empty/whitespace stages are normalised to "(no history)" too.
 *
 *  @see computeLostByStage for the probability-bucket variant
 *    (Discovery 5-29%, Qualifying 30-49%, etc.) — these serve different
 *    UI surfaces and intentionally coexist. */
export function computeLostByPreCloseStage(
  rows: readonly OpportunityRow[],
): LostByPreCloseStageRow[] {
  const NO_HISTORY = "(no history)";
  const buckets = new Map<string, { count: number; amount: number }>();
  for (const r of rows) {
    if (!isLost(r)) continue;
    const raw = r.pre_close_max_stage;
    const stage =
      typeof raw === "string" && raw.trim() !== "" ? raw.trim() : NO_HISTORY;
    const b = buckets.get(stage) ?? { count: 0, amount: 0 };
    b.count += 1;
    // Prefer peak_amount when present; fall back to the row's current
    // amount. Negative values are dropped — they're SF data-bug
    // artefacts (a Salesforce amount should never be < 0). Pre-PR
    // security + test-automator LOW.
    const peak = r.peak_amount;
    const amt = typeof peak === "number" && Number.isFinite(peak) && peak >= 0
      ? peak
      : typeof r.amount === "number" && Number.isFinite(r.amount) && r.amount >= 0
        ? r.amount
        : 0;
    b.amount += amt;
    buckets.set(stage, b);
  }
  return [...buckets.entries()]
    .map(([stage, v]) => ({ stage, count: v.count, amount: v.amount }))
    .sort((a, b) => b.count - a.count || b.amount - a.amount);
}

// ---------- SGNL-213 — Probability-weighted Revenue Projection ----------
//
// Operator feedback: raw open-pipeline totals overstate the forecast
// because a $1M opp at 10% probability is really $100K of expected
// revenue, not $1M. This computes the probability-weighted projection
// with per-stage breakdown so operators can see how the formula
// deflates each stage.
//
// Formula: projected = Σ(amount × probability/100) over OPEN opps with
// `probability >= min` (default 5% — drops MQL / Stage-0 noise).
//
// "Open" here means NOT closed: `is_closed !== true`. Closed Won opps
// are not in the projection — they're already revenue, not forecast.

export interface RevenueProjectionStageRow {
  stage: string;
  count: number;
  /** Σ amount (raw SF opp amount, unweighted). */
  raw_amount: number;
  /** Σ (amount × probability/100). The deflated forecast. */
  weighted_amount: number;
}

export interface RevenueProjection {
  /** Total weighted forecast across every contributing opp. */
  headline_total: number;
  /** Per-stage rows ordered by raw_amount DESC. */
  per_stage: RevenueProjectionStageRow[];
  /** How many opps contributed to the projection (excludes filtered-out). */
  contributing_opps: number;
  /** Σ raw amount (for the "deflation ratio" callout in the view). */
  total_raw: number;
}

export function computeWeightedRevenueProjection(
  rows: readonly OpportunityRow[],
  min: number | null,
): RevenueProjection {
  const lower = min ?? 0;
  const buckets = new Map<string, RevenueProjectionStageRow>();
  let headline = 0;
  let total_raw = 0;
  let count = 0;
  for (const r of rows) {
    if (!isOpen(r)) continue;
    // Security LOW pre-PR review: enforce [0, 100] bounds — a
    // probability > 100 (BE schema drift) would otherwise multiply
    // amounts by > 1.0 and silently inflate the forecast.
    if (!isBoundedProbability(r.probability)) continue;
    if (r.probability < lower) continue;
    const amt = isFiniteProb(r.amount) ? r.amount : 0;
    const weighted = amt * (r.probability / 100);
    // PR-Agent #132 (imp 5): guard against non-string stage (BE
    // schema drift would otherwise throw on `.trim()` and crash the
    // entire projection render). Non-strings coerce to "(unset)".
    const stage = (typeof r.stage === "string" ? r.stage : "").trim() || "(unset)";
    let b = buckets.get(stage);
    if (!b) {
      b = { stage, count: 0, raw_amount: 0, weighted_amount: 0 };
      buckets.set(stage, b);
    }
    b.count++;
    b.raw_amount += amt;
    b.weighted_amount += weighted;
    headline += weighted;
    total_raw += amt;
    count++;
  }
  const per_stage = [...buckets.values()].sort(
    (a, b) => b.raw_amount - a.raw_amount,
  );
  return {
    headline_total: headline,
    per_stage,
    contributing_opps: count,
    total_raw,
  };
}

// ---------- SGNL-26 — Partner Opportunities ----------
//
// Group opportunities by tag_key (e.g. "snowflake", "aws"), counting opps
// and summing $$$ that carry a yes-verdict above a confidence floor. Same
// confidence floor as the global tag filter (default 0.6) so the count on
// this tab matches what Deal Explorer shows when the same tag is selected.

export interface PartnerBreakdownRow {
  tag_key: string;
  opp_count: number;
  total_amount: number;
  open_amount: number;
  won_amount: number;
}

export function computePartnerBreakdown(
  rows: OpportunityRow[],
  threshold = 0.6,
): PartnerBreakdownRow[] {
  const buckets = new Map<string, PartnerBreakdownRow>();
  for (const r of rows) {
    for (const t of r.tags ?? []) {
      if (t.verdict !== "yes") continue;
      if (t.confidence < threshold) continue;
      let b = buckets.get(t.tag_key);
      if (!b) {
        b = {
          tag_key: t.tag_key,
          opp_count: 0,
          total_amount: 0,
          open_amount: 0,
          won_amount: 0,
        };
        buckets.set(t.tag_key, b);
      }
      b.opp_count++;
      const amt = r.amount ?? 0;
      b.total_amount += amt;
      if (r.is_won === true) b.won_amount += amt;
      else if (r.is_closed !== true) b.open_amount += amt;
    }
  }
  return [...buckets.values()].sort((a, b) => b.total_amount - a.total_amount);
}
